import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import { useProgressToast } from './helpers/useProgressToast'
import { SiteRequirementsPatchBody } from '@black-bear-energy/black-bear-energy-common'

type UpdateSiteRequirementsVariables = SiteRequirementsPatchBody & {
  /**
   * The site RFP being edited. The endpoint is keyed on the site RFP and
   * the site id is fetched from the site RFP record on the backend.
   */
  siteRFPId: number
  siteName: string
  onSuccess?: () => void
}

/**
 * Update the Site Requirements card for a given site RFP.
 * @returns A UseMutationResult object for triggering the PATCH.
 */
export const useUpdateSiteRequirements = (): UseMutationResult<
  void,
  Error,
  UpdateSiteRequirementsVariables
> => {
  const api = useApiWithIDToken()
  const queryClient = useQueryClient()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  return useMutation<
    void,
    Error,
    UpdateSiteRequirementsVariables,
    { toastId: string }
  >({
    mutationFn: ({ siteRFPId, siteName, onSuccess, ...fields }) =>
      updateSiteRequirements({ api, siteRFPId, fields }),
    onMutate: (vars) => ({
      toastId: startProgress(
        'Updating Site Requirements',
        `Updating ${vars.siteName}`
      ),
    }),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPDetails'],
        refetchType: 'active',
      })
      sendNotification({
        title: 'Update Successful',
        message: `Successfully updated ${vars.siteName}.`,
        useRucksackUI: true,
      })
      vars.onSuccess?.()
    },
    onError: (error, vars) => {
      sendNotification({
        title: 'Update Failed',
        message: `An error occurred while trying to update Site Requirements for ${vars.siteName}. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
    onSettled: (_data, _error, _vars, context) => {
      endProgress(context?.toastId)
    },
  })
}

async function updateSiteRequirements({
  api,
  siteRFPId,
  fields,
}: {
  api: ReturnType<typeof useApiWithIDToken>
  siteRFPId: number
  fields: SiteRequirementsPatchBody
}): Promise<void> {
  await api.patch(`/site-rfps/${siteRFPId}/requirements`, fields)
}
